import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as callStatusesApi from './call-statuses.api';

import type {
  CallStatusListQuery,
  CreateCallStatusRequest,
  UpdateCallStatusRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: CallStatusListQuery) => ['call-statuses', 'list', query] as const,
  detail: (id: string) => ['call-statuses', 'detail', id] as const,
};

export const useCallStatusesList = (query: CallStatusListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => callStatusesApi.listCallStatuses(query),
    staleTime: 30_000,
  });

export const useCreateCallStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCallStatusRequest) => callStatusesApi.createCallStatus(payload),
    onSuccess: (cs) => {
      qc.invalidateQueries({ queryKey: ['call-statuses', 'list'] });
      toast.success(`Added ${cs.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add call status'),
  });
};

export const useUpdateCallStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCallStatusRequest }) =>
      callStatusesApi.updateCallStatus(id, payload),
    onSuccess: (cs) => {
      qc.invalidateQueries({ queryKey: ['call-statuses', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(cs.id) });
      toast.success('Call status updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update call status'),
  });
};

export const useDeleteCallStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => callStatusesApi.deleteCallStatus(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['call-statuses', 'list'] });
      toast.success('Call status deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete call status'),
  });
};

export const useBulkDeleteCallStatuses = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => callStatusesApi.bulkDeleteCallStatuses(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['call-statuses', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete call statuses'),
  });
};

export const useBulkSetCallStatusesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      callStatusesApi.bulkSetCallStatusesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['call-statuses', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update call status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
